import { Box, Stack, type StackProps, useTheme } from "@mui/material";
import { FoodsTextInput, Typography, type FoodsTextInputProps } from "@foodpilot/foods";
import { useId, type ReactNode } from "react";

type FoodsFieldLabelProps = {
    id?: string;
    label: string;
    required?: boolean;
};

export const formatFieldLabel = (label: string, required = false) => {
    const withoutColon = label
        .trimEnd()
        .replace(/\s*:\s*$/, "")
        .trimEnd();
    const hasRequiredMarker = withoutColon.endsWith("*");
    const withoutRequiredMarker = withoutColon.replace(/\s*\*\s*$/, "").trimEnd();

    return `${withoutRequiredMarker}${required || hasRequiredMarker ? "*" : ""}`;
};

export const FoodsFieldLabel = ({ id, label, required = false }: FoodsFieldLabelProps) => {
    const theme = useTheme();

    return (
        <Typography id={id} variant="body-semibold" sx={{ color: theme.custom.grey[2800] }}>
            {formatFieldLabel(label, required)}
        </Typography>
    );
};

export type FoodsFieldProps = {
    label?: string;
    required?: boolean;
    helperText?: string;
    errorText?: string;
    labelId?: string;
    descriptionId?: string;
    announceError?: boolean;
    children: ReactNode;
    stackProps?: StackProps;
};

export type FoodsLoadingIndicatorProps = {
    label: string;
    color?: string;
    size?: number | string;
};

export type FoodsFieldSkeletonProps = {
    label: string;
    height: number | string;
};

/** Shared token-based placeholder for a field group; avoids a second visual-system dependency. */
export const FoodsFieldSkeleton = ({ label, height }: FoodsFieldSkeletonProps) => {
    const theme = useTheme();

    return (
        <Box
            aria-label={label}
            role="status"
            sx={{
                animation: `foods-field-skeleton ${theme.transitions.duration.complex}ms ${theme.transitions.easing.easeInOut} infinite`,
                backgroundImage: `linear-gradient(90deg, ${theme.custom.grey[300]} 0%, ${theme.custom.grey[500]} 50%, ${theme.custom.grey[300]} 100%)`,
                backgroundSize: "200% 100%",
                borderRadius: theme.spacing(0.5),
                height,
                width: "100%",
                "@keyframes foods-field-skeleton": {
                    from: { backgroundPosition: "200% 0" },
                    to: { backgroundPosition: "-200% 0" },
                },
                "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                },
            }}
        />
    );
};

/** Theme-aligned indeterminate progress used when FOODS has no dedicated loader primitive. */
export const FoodsLoadingIndicator = ({ label, color, size }: FoodsLoadingIndicatorProps) => {
    const theme = useTheme();
    const resolvedSize = size ?? theme.spacing(2);

    return (
        <Box
            aria-label={label}
            component="span"
            role="progressbar"
            sx={{
                animation: `foods-field-spin ${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut} infinite`,
                border: `${theme.spacing(0.25)} solid ${color ?? theme.palette.primary[600]}`,
                borderRadius: "50%",
                borderTopColor: "transparent",
                boxSizing: "border-box",
                display: "inline-block",
                flexShrink: 0,
                height: resolvedSize,
                width: resolvedSize,
                "@keyframes foods-field-spin": {
                    to: { transform: "rotate(360deg)" },
                },
            }}
        />
    );
};

/** Shared visual field wrapper. MUI is intentionally limited to layout. */
export const FoodsField = ({
    label,
    required = false,
    helperText,
    errorText,
    labelId,
    descriptionId,
    announceError = false,
    children,
    stackProps,
}: FoodsFieldProps) => {
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(1)} {...stackProps}>
            {label !== undefined && <FoodsFieldLabel id={labelId} label={label} required={required} />}
            {children}
            {(errorText || helperText) && (
                <Typography
                    id={descriptionId}
                    role={announceError && errorText ? "alert" : undefined}
                    variant="small"
                    sx={{ color: errorText ? theme.custom.red[600] : theme.custom.grey[1400] }}
                >
                    {errorText ?? helperText}
                </Typography>
            )}
        </Stack>
    );
};

export type FoodsTextFieldProps = {
    label?: string;
    required?: boolean;
    helperText?: string;
    errorText?: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
    disabled?: boolean;
    unit?: string;
    unitAddon?: ReactNode;
    extraInputProps?: FoodsTextInputProps["ExtraInputProps"];
    stackProps?: StackProps;
};

export const FoodsTextField = ({
    label,
    required = false,
    helperText,
    errorText,
    placeholder,
    value,
    onChange,
    disabled = false,
    unit,
    unitAddon,
    extraInputProps,
    stackProps,
}: FoodsTextFieldProps) => {
    const generatedId = useId();
    const inputId = extraInputProps?.id ?? `foods-field-${generatedId}`;
    const inputProps =
        typeof extraInputProps?.inputProps === "object" && extraInputProps.inputProps !== null ?
            extraInputProps.inputProps
        :   {};

    return (
        <FoodsField
            label={label}
            required={required}
            helperText={helperText}
            errorText={errorText}
            stackProps={stackProps}
        >
            <FoodsTextInput
                value={value}
                onChange={onChange}
                disabled={disabled}
                error={Boolean(errorText)}
                unit={unit}
                unitAddon={unitAddon}
                ExtraInputProps={{
                    ...extraInputProps,
                    id: inputId,
                    placeholder: extraInputProps?.placeholder ?? placeholder,
                    inputProps: {
                        "aria-label": label,
                        ...inputProps,
                    },
                }}
                StackProps={{ width: "100%" }}
            />
        </FoodsField>
    );
};
